import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import CanalLeafletMap from "@/components/CanalLeafletMap";
import EditorialMarquee from "@/components/home/EditorialMarquee";
import UnescoMarquee from "@/components/UnescoMarquee";
import { useLang } from "@/i18n/LanguageContext";

/**
 * Page Parcours unifiée — fusion de l'ancien /parcours (3 lectures) et /sites (6 escales).
 * Deux sections mises en évidence :
 *   1. § Parcours 2026 · 6 escales (édition en cours)
 *   2. § Horizon 2030 · Le parcours complet (240/500/750 km + carte)
 */

const READINGS = {
  fr: [
    { km: "240 km", title: "Le Canal du Midi", subtitle: "Fluvial · UNESCO 1996", body: "Le canal historique creusé à la pioche par Riquet entre 1666 et 1681 — de Toulouse (port de l'Embouchure) à Marseillan sur l'Étang de Thau. C'est LUI qui est classé au patrimoine mondial de l'UNESCO.", highlight: "63 écluses · 126 ponts · 7 aqueducs" },
    { km: "500 km", title: "Le Canal des 2 Mers", subtitle: "Fluvial · Bordeaux → Sète", body: "L'itinéraire navigable complet, en enchaînant deux ouvrages : la Garonne (Bordeaux-Castets), puis le Canal latéral à la Garonne (Castets-Toulouse, 193 km) et enfin le Canal du Midi (Toulouse-Sète, 240 km). C'est ce qu'on peut relier en péniche.", highlight: "Le tracé praticable en bateau" },
    { km: "750 km", title: "Le Fil du Midi · FILM", subtitle: "Cyclable · Pédestre · Fluvial · Rigoles", body: "Le parcours artistique complet — voie cyclable V80 continue, chemins de halage, canal + rigoles historiques de la Montagne Noire, branches vers Saint-Ferréol et le Bassin de Naurouze. Le Fil épouse toutes ces lignes pour tisser un ruban d'art de l'Atlantique à la Méditerranée.", highlight: "L'itinéraire artistique 2026 → 2030" },
  ],
  en: [
    { km: "240 km", title: "The Canal du Midi", subtitle: "Waterway · UNESCO 1996", body: "The historic canal dug by pickaxe under Riquet between 1666 and 1681 — from Toulouse (port de l'Embouchure) to Marseillan on the Étang de Thau. THIS is what is listed as UNESCO World Heritage.", highlight: "63 locks · 126 bridges · 7 aqueducts" },
    { km: "500 km", title: "The Canal des 2 Mers", subtitle: "Waterway · Bordeaux → Sète", body: "The full navigable route, chaining two works: the Garonne (Bordeaux-Castets), then the Canal latéral à la Garonne (Castets-Toulouse, 193 km) and finally the Canal du Midi (Toulouse-Sète, 240 km). This is what you can travel by barge.", highlight: "The route navigable by boat" },
    { km: "750 km", title: "Le Fil du Midi · FILM", subtitle: "Cycling · Walking · Waterway · Channels", body: "The complete art trail — the continuous V80 cycle route, towpaths, canal + historic channels of the Montagne Noire, branches to Saint-Ferréol and the Naurouze basin. The Thread embraces all these lines to weave a ribbon of art from the Atlantic to the Mediterranean.", highlight: "The artistic route 2026 → 2030" },
  ],
};

const KEYFACTS = {
  fr: [
    ["1666 → 1681", "Construction · 15 ans, 12 000 ouvriers"],
    ["189 m", "Seuil de Naurouze · point culminant"],
    ["1996", "Inscription UNESCO · 30 ans en 2026"],
    ["42 000", "Platanes plantés le long du canal"],
  ],
  en: [
    ["1666 → 1681", "Construction · 15 years, 12,000 workers"],
    ["189 m", "Naurouze threshold · highest point"],
    ["1996", "UNESCO inscription · 30 years in 2026"],
    ["42,000", "Plane trees planted along the canal"],
  ],
};

export default function ParcoursPage() {
  const { lang } = useLang();
  const en = lang === "en";
  const readings = READINGS[lang];
  const keyFacts = KEYFACTS[lang];
  const [sites, setSites] = useState([]);
  useEffect(() => { api.get("/sites").then(r => setSites(r.data)).catch(() => {}); }, []);

  return (
    <main className="pt-24" data-testid="parcours-page">
      <UnescoMarquee />

      {/* Hero */}
      <section className="px-6 md:px-12 max-w-[1400px] mx-auto pt-16 pb-8">
        <div className="meta-label boxed text-cinabre">§ {en ? "The Trail" : "Parcours"}</div>
        <h1 className="font-serif font-light text-6xl md:text-8xl leading-[0.9] tracking-tight mt-8 max-w-4xl">
          {en ? <>From <em className="italic">Castets</em> to<br/>the <em className="italic">Étang de Thau</em>.</> : <>De <em className="italic">Castets</em> à<br/>l'<em className="italic">Étang de Thau</em>.</>}
        </h1>
        <p className="mt-8 max-w-2xl text-fg/75 leading-relaxed text-lg">
          {en ? <>A trail on two scales: <strong>the 2026 edition with 6 concrete stops</strong>, and by <strong>2030 full coverage of the 750 km</strong> from the Atlantic to the Mediterranean.</> : <>Un parcours à deux échelles : <strong>l'édition 2026 avec 6 escales concrètes</strong>, et à <strong>l'horizon 2030 la couverture complète des 750 km</strong> de l'Atlantique à la Méditerranée.</>}
        </p>
      </section>

      {/* SECTION 1 · PARCOURS 2026 · 6 ESCALES */}
      <section id="parcours-2026" className="mt-20 md:mt-28 px-6 md:px-12 max-w-[1400px] mx-auto scroll-mt-32" data-testid="parcours-2026">
        <div className="border-t border-fg/15 pt-14">
          <div className="meta-label boxed text-cinabre">§ {en ? "2026 Trail · Current edition" : "Parcours 2026 · Édition en cours"}</div>
          <h2 className="mt-8 font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {en ? <>Six stops, one <em className="italic">line</em>.</> : <>Six escales, une <em className="italic">ligne</em>.</>}
          </h2>
          <p className="mt-6 max-w-2xl text-fg/75 leading-relaxed text-lg">
            {en ? "Six heritage sites punctuate the 2026 edition. On each, a curatorial programme entrusted to a curator — 30 artists per stop compose an open-air exhibition." : "Six sites patrimoniaux ponctuent l'édition 2026. Sur chacun, une programmation curatoriale confiée à un commissaire — 30 artistes par escale composent une exposition à ciel ouvert."}
          </p>
        </div>

        <div className="mt-20 space-y-32 md:space-y-40">
          {sites.map((s, i) => (
            <motion.article
              key={s.slug}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9 }}
              className="grid md:grid-cols-12 gap-8 md:gap-10 items-start"
              data-testid={`site-detail-${s.slug}`}
            >
              <div className={`md:col-span-5 overflow-hidden border border-fg/15 ${i % 2 ? "md:order-3" : "md:order-1"}`}>
                <img src={s.image} alt={s.name} className="w-full aspect-[4/5] object-cover" />
              </div>
              <div className="md:col-span-4 md:order-2 self-center">
                <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-cinabre">{en ? "Chapter" : "Chapitre"} {s.chapter} · {s.km}</div>
                <h3 className="font-serif text-5xl md:text-6xl mt-4 leading-[0.95] tracking-tight">{s.name}</h3>
                <div className="font-mono text-[11px] tracking-[0.24em] uppercase mt-2 text-fg/60">{s.role}</div>
                <p className="mt-8 text-fg/80 leading-relaxed text-lg">{s.description}</p>
                <div className="mt-8 border-t border-fg/20 pt-6 font-serif text-2xl italic">{en ? "30 artists · 30 years UNESCO · a chapter of the Thread" : "30 artistes · 30 ans UNESCO · un chapitre du Fil"}</div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">#30CMDUNESCO</div>
                <Link to={`/sites/${s.slug}`} className="btn-editorial ghost mt-8" data-testid={`site-cta-${s.slug}`}>
                  {en ? "Discover the site →" : "Découvrir le site →"}
                </Link>
              </div>
              <div className={`md:col-span-3 ${i % 2 ? "md:order-1" : "md:order-3"}`}>
                {s.poster ? (
                  <div className="border border-fg/15 overflow-hidden bg-fg/5">
                    <img src={s.poster} alt={`Affiche ${s.name}`} className="w-full aspect-[2/3] object-cover" loading="lazy" />
                    <div className="p-3 font-mono text-[9px] tracking-[0.24em] uppercase text-fg/60">{s.poster_final ? (en ? `Official poster · ${s.name}` : `Affiche officielle · ${s.name}`) : (en ? "Provisional poster" : "Affiche provisoire")}</div>
                  </div>
                ) : (
                  <div className="border border-fg/15 aspect-[2/3] flex items-center justify-center bg-fg/[0.03]">
                    <div className="text-center px-4">
                      <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-fg/50">{en ? "Poster" : "Affiche"}</div>
                      <div className="mt-2 font-serif italic text-lg text-fg/60">{en ? "coming soon" : "bientôt dévoilée"}</div>
                    </div>
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* SECTION 2 · HORIZON 2030 · LE PARCOURS COMPLET */}
      <section id="horizon-2030" className="mt-32 md:mt-44 px-6 md:px-12 max-w-[1400px] mx-auto scroll-mt-32" data-testid="horizon-2030">
        <div className="border-t border-fg/15 pt-14">
          <div className="meta-label boxed text-cinabre">§ {en ? "Horizon 2030 · The complete trail" : "Horizon 2030 · Le parcours complet"}</div>
          <h2 className="mt-8 font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            {en ? <>Eventually, <em className="italic">750 km</em> of continuity.</> : <>À terme, <em className="italic">750 km</em> continus.</>}
          </h2>
          <p className="mt-6 max-w-3xl text-fg/75 leading-relaxed text-lg">
            {en ? <>Why do some say 240 km, others 500 km, and we 750 km? It is not a contradiction — it is a <em>matter of reading</em>. Three ways of counting, three experiences of the same territory.</> : <>Pourquoi certains disent 240 km, d'autres 500 km, et nous 750 km ? Ce n'est pas une contradiction — c'est une <em>question de lecture</em>. Trois manières de compter, trois expériences du même territoire.</>}
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-4 md:gap-6" data-testid="parcours-lectures">
          {readings.map((r, i) => (
            <motion.article
              key={r.km}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.08 }}
              className={`border border-fg/15 p-8 md:p-10 flex flex-col ${i === 2 ? "bg-fg text-bg" : ""}`}
              data-testid={`lecture-${i}`}
            >
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">{r.subtitle}</div>
              <div className={`font-serif text-6xl md:text-7xl leading-none tracking-tight mt-6 ${i === 2 ? "text-cinabre" : ""}`}>{r.km}</div>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mt-4">{r.title}</h3>
              <p className="mt-6 leading-relaxed opacity-85 flex-1">{r.body}</p>
              <div className={`mt-8 pt-6 border-t ${i === 2 ? "border-bg/20" : "border-fg/15"} font-mono text-[10px] tracking-[0.24em] uppercase opacity-80`}>
                {r.highlight}
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="meta-label text-cinabre">§ {en ? "What to remember" : "Ce qu'il faut retenir"}</div>
            <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight mt-4">
              {en ? <>240 · 500 · 750:<br/><em className="italic">the same</em> line, read three ways.</> : <>240 · 500 · 750 :<br/><em className="italic">la même</em> ligne, lue de trois manières.</>}
            </h3>
          </div>
          <div className="md:col-span-7 text-fg/85 text-lg leading-relaxed space-y-4">
            {en ? (
              <>
                <p>The <strong className="text-cinabre">Canal du Midi</strong> (240 km) is the heritage masterpiece. The <strong className="text-cinabre">Canal des 2 Mers</strong> (500 km) is what you can navigate without transshipment. The <strong className="text-cinabre">Fil du Midi</strong> (750 km) adds what the boat cannot see: cycle routes, towpaths, the channels of the Montagne Noire, branches to Saint-Ferréol and Naurouze.</p>
                <p>Every walker, every cyclist, every boater, every riverside resident — <em>each</em> has their own Thread. Our art trail connects them all.</p>
              </>
            ) : (
              <>
                <p>Le <strong className="text-cinabre">Canal du Midi</strong> (240 km) est le chef-d'œuvre patrimonial. Le <strong className="text-cinabre">Canal des 2 Mers</strong> (500 km) est ce qu'on peut naviguer sans transbordement. Le <strong className="text-cinabre">Fil du Midi</strong> (750 km) ajoute ce que le bateau ne voit pas : voies cyclables, chemins de halage, rigoles de la Montagne Noire, branches vers Saint-Ferréol et Naurouze.</p>
                <p>Chaque marcheur, chaque cycliste, chaque plaisancier, chaque riverain — <em>chacun</em> a son Fil. Notre parcours artistique les relie tous.</p>
              </>
            )}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-fg/15 border border-fg/15">
          {keyFacts.map(([k, v]) => (
            <div key={k} className="bg-bg p-6 md:p-8">
              <div className="font-serif text-4xl md:text-5xl leading-none tracking-tight text-cinabre">{k}</div>
              <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">{v}</div>
            </div>
          ))}
        </div>

        <p className="mt-10 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 max-w-2xl">
          Sources · Voies Navigables de France (VNF) · UNESCO · L'Écluse au Soleil · Office de Tourisme du Canal du Midi
        </p>
      </section>

      <CanalLeafletMap height="640px" />

      <EditorialMarquee inverted />
    </main>
  );
}
